/**
 * Heartbeat WebSocket client — req Module 10. Pings the server every 30s;
 * the server re-validates the session on every ping (routes_heartbeat.py)
 * and sends `{"type": "session_invalid"}` then closes if it was revoked
 * mid-connection (an admin suspending the account is the concrete trigger).
 * On any other drop, reconnects with capped exponential backoff — a real
 * network hiccup shouldn't need a page reload to recover from.
 */

const PING_INTERVAL_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

interface HeartbeatMessage {
  type: "pong" | "session_invalid";
}

/** Opens the heartbeat socket for `gameId`. Returns a disconnect function —
 *  call it on unmount/game-exit to stop pinging and cancel any pending
 *  reconnect. `onSessionInvalid` fires at most once, when the server
 *  actually revokes the session (not on ordinary connection drops). */
export function connectHeartbeat(gameId: string, onSessionInvalid: () => void): () => void {
  let ws: WebSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = INITIAL_BACKOFF_MS;
  let stopped = false;

  const clearTimers = () => {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    reconnectTimer = setTimeout(() => {
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
      connect();
    }, backoffMs);
  };

  const connect = () => {
    if (stopped) return;
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    ws = new WebSocket(`${scheme}//${window.location.host}/api/heartbeat?game=${encodeURIComponent(gameId)}`);

    ws.onopen = () => {
      backoffMs = INITIAL_BACKOFF_MS;
      pingTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      let data: HeartbeatMessage;
      try {
        data = JSON.parse(event.data as string);
      } catch {
        return; // malformed frame — ignore
      }
      if (data.type === "session_invalid") {
        stopped = true; // server revoked us — don't reconnect
        clearTimers();
        onSessionInvalid();
      }
    };

    // A WS error is always followed by onclose, which already drives
    // reconnect — no separate onerror handling needed.
    ws.onclose = () => {
      if (pingTimer !== null) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      if (!stopped) scheduleReconnect();
    };
  };

  connect();

  return () => {
    stopped = true;
    clearTimers();
    ws?.close();
    ws = null;
  };
}
